import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, UserCheck, FileCheck2, CheckCircle2, Target, ArrowRight, Link2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const BE = process.env.REACT_APP_BACKEND_URL;

// The four activity targets the CEO wants front-and-centre on the overview.
// Keys map to Game Plan funnel stages so the numbers match the tracker exactly.
const CARDS = [
  { key: 'portals_sent', label: 'Portal Invites', icon: Send, accent: 'sky' },
  { key: 'portals', label: 'Portal Completions', icon: UserCheck, accent: 'violet' },
  { key: 'lodgements', label: 'Application Lodgements', icon: FileCheck2, accent: 'amber' },
  { key: 'approvals', label: 'Application Approvals', icon: CheckCircle2, accent: 'emerald' },
];

const ACCENT = {
  sky: 'text-sky-400', violet: 'text-violet-400', amber: 'text-amber-300', emerald: 'text-emerald-400',
};
const STATUS = {
  ahead: { bar: 'bg-emerald-400', text: 'text-emerald-400', label: 'ahead' },
  on_track: { bar: 'bg-emerald-400', text: 'text-emerald-400', label: 'on track' },
  behind: { bar: 'bg-rose-400', text: 'text-rose-400', label: 'behind' },
  no_target: { bar: 'bg-slate-500', text: 'text-slate-400', label: '—' },
};

const Bar = ({ pct, tone }) => (
  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
    <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
  </div>
);

// One period line (Weekly or Monthly): actual / target, % and a progress bar.
const PeriodLine = ({ label, actual, target, status }) => {
  const st = STATUS[status] || STATUS.no_target;
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <span className="text-xs tabular-nums text-slate-300"><b className="text-white">{actual}</b> / {target} <span className={cn('font-bold', st.text)}>· {pct}%</span></span>
      </div>
      <Bar pct={pct} tone={st.bar} />
    </div>
  );
};

const Card = ({ icon: Icon, label, accent, stage }) => {
  const monthlyTarget = stage?.target_monthly ?? 0;
  const weeklyTarget = stage?.target_weekly ?? 0;
  const monthlyActual = stage?.actual_month ?? 0;
  const weeklyActual = stage?.actual_week ?? 0;
  const monthStatus = stage?.status_month || 'no_target';
  const remaining = Math.max(0, monthlyTarget - monthlyActual);
  const st = STATUS[monthStatus] || STATUS.no_target;
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 flex flex-col gap-3" data-testid={`ceo-target-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
          <Icon className={cn('w-4 h-4', ACCENT[accent])} /> {label}
        </div>
        <span className={cn('text-[10px] font-bold uppercase tracking-wide', st.text)} data-testid={`ceo-target-status-${label.replace(/\s/g, '-').toLowerCase()}`}>{st.label}</span>
      </div>
      <PeriodLine label="This month" actual={monthlyActual} target={monthlyTarget} status={monthStatus} />
      <PeriodLine label="This week" actual={weeklyActual} target={weeklyTarget} status={stage?.status_week || 'no_target'} />
      <div className="text-[11px] text-slate-500 pt-0.5 border-t border-white/5">
        {remaining > 0 ? <><b className="text-slate-300">{remaining}</b> more this month to hit target</> : <span className="text-emerald-400 font-bold">Monthly target reached</span>}
      </div>
    </div>
  );
};

// Weekly & monthly activity targets with live pod-wide actuals, pulled from the
// Game Plan scoreboard so the CEO sees exactly how the company is tracking.
export const WeeklyMonthlyTargets = ({ token, month }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    if (!token || !month) return;
    setErr(false);
    try {
      const r = await fetch(`${BE}/api/gameplan/activity/scoreboard/${month}?scope=company`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('failed');
      setData(await r.json());
    } catch (_) { setErr(true); }
  }, [token, month]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return null;
  const byKey = {};
  (data?.stages || []).forEach((s) => { byKey[s.key] = s; });
  const hasTarget = (data?.volume_target || 0) > 0;

  return (
    <section data-testid="ceo-weekly-monthly-targets">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-extrabold flex items-center gap-1.5">
          <Target className="w-4 h-4 text-emerald-400" /> Weekly &amp; Monthly Targets
          <span className="text-[11px] font-medium text-slate-500">· pod tracking, live</span>
        </h3>
        <Link to="/game-plan" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400 hover:underline" data-testid="ceo-targets-source">
          <Link2 className="w-3 h-3" /> Live from Game Plan funnel <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {!hasTarget ? (
        <div className="rounded-xl border border-sky-400/30 bg-sky-500/10 p-4 text-xs text-slate-300" data-testid="ceo-targets-empty">
          No monthly settlement volume target set in the Game Plan Tracker yet — set one to see how many portal invites, completions, lodgements and approvals the pod needs each week and month, and how it's tracking.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CARDS.map((c) => <Card key={c.key} icon={c.icon} label={c.label} accent={c.accent} stage={byKey[c.key]} />)}
        </div>
      )}
    </section>
  );
};

export default WeeklyMonthlyTargets;
